import { useMemo, useState } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';

import { API_URL, MESSAGE } from 'constant';
import { UserSubscriptionsState } from 'global-stores';
import { useNetwork, useNotification } from 'hooks';
import { IdentityType, IndetityFlowNavigate } from '../constants';
import {
	IdentityFlowCompletion,
	IdentityFlowError,
	SubscriptionIdentityNaviagtion,
} from '../store';
import { useBillingDetails } from './use-processing';
import { useUserInit } from './use-user';

export const useError = () => {
	const userSubscriptions = useRecoilValue(UserSubscriptionsState);

	const [isLoading, setIsLoading] = useState(false);
	const { post } = useNetwork({
		updateState: false,
		returnResponse: true,
	});

	const { initBillingApi } = useBillingDetails();
	const { initUser } = useUserInit();
	const { errorNotification } = useNotification();
	/* The screen this hook drives now PRINTS the reason, so a retry has to keep
	   it honest: a second failure replaces it, and a success clears it and
	   leaves the error screen. Retry used to do neither — a successful retry
	   subscribed the business and left it staring at "Oops! Something went
	   wrong", and a second, different failure was announced only by a toast
	   while the page still showed the first reason. */
	const setFlowError = useSetRecoilState(IdentityFlowError);
	const setNavigate = useSetRecoilState(SubscriptionIdentityNaviagtion);
	const setCompletion = useSetRecoilState(IdentityFlowCompletion);

	const hasSubscribedPerson = useMemo(
		() =>
			userSubscriptions[0]?.[IdentityType.Individual] &&
			userSubscriptions[0]?.[IdentityType.Individual].status,
		[userSubscriptions]
	);

	const onRetry = async () => {
		setIsLoading(true);
		let resp;
		/*
		 * THE SAME FLAG THE FIRST ATTEMPT SENT. These two branches were the
		 * inverse of `useProcessing`'s, so Retry always asked for the opposite
		 * of what had just been tried: a first-time entity retried as an
		 * UPGRADE and got "No subscription is there currently so can not able
		 * to upgrade", while an upgrading account retried as a fresh subscribe
		 * and got "Invalid request for upgradation of account". Retry could not
		 * succeed in either direction — which the error screen now makes worse
		 * by printing the nonsensical reason and inviting another go.
		 */
		if (hasSubscribedPerson)
			resp = await post(API_URL.SUBSCRIPTIONS, { upgrade: true });
		else resp = await post(API_URL.SUBSCRIPTIONS, {});
		if (resp?.success) {
			await initBillingApi();
			await initUser();
			setFlowError('');
			setCompletion('subscription');
			setNavigate(IndetityFlowNavigate.Success);
		} else {
			const reason = resp?.errorData?.message || MESSAGE.ERROR;
			errorNotification(reason);
			/* The row carries the SERVER's reason, so it holds nothing when there
			   isn't one: with the generic fallback stored, an outage printed
			   "Oops! Something went wrong" as the heading and again beneath it. */
			setFlowError(resp?.errorData?.message ?? '');
		}
		setIsLoading(false);
	};

	return {
		isLoading,
		onRetry,
	};
};
